import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, FileEdit, Images, LogOut, ExternalLink, BarChart3, Sun, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePrefs } from "../context/PrefsContext";
import { LOGO_URL } from "../lib/api";
import { EnquiriesTable } from "../components/admin/EnquiriesTable";
import { ContentEditor } from "../components/admin/ContentEditor";
import { GalleryManager } from "../components/admin/GalleryManager";
import { AnalyticsDashboard } from "../components/admin/AnalyticsDashboard";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = usePrefs();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const tabBtn = (key, label, Icon) => (
    <button
      onClick={() => setTab(key)}
      data-testid={`admin-tab-${key}`}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
        tab === key
          ? "bg-orange-600 text-white shadow-md shadow-orange-600/20"
          : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-white/60 dark:bg-transparent hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-transparent"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 dark:border-white/10 bg-white/95 dark:bg-gradient-to-r dark:from-[#0b1533] dark:via-[#0b1533] dark:to-slate-950 sticky top-0 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="TMG" className="h-9 w-auto dark:brightness-0 dark:invert" />
            <span className="font-heading font-bold text-slate-900 dark:text-white text-sm hidden sm:flex items-center gap-2 border-l border-slate-200 dark:border-white/10 pl-3 ml-1">
              <LayoutDashboard size={16} className="text-orange-600 dark:text-orange-500" /> Panel Admin TMG
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="admin-theme-toggle"
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              title={theme === "dark" ? "Tukar ke Mod Terang" : "Tukar ke Mod Gelap"}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <a href="/" target="_blank" rel="noreferrer" className="text-slate-500 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-500 text-sm flex items-center gap-1.5 transition-colors" data-testid="view-site-link">
              <ExternalLink size={14} /> <span className="hidden sm:inline">Lihat Laman</span>
            </a>
            <span className="text-slate-500 text-sm hidden md:block" data-testid="admin-email">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-500 transition-colors"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Log Keluar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-3 mb-8 flex-wrap">
          {tabBtn("analytics", "Analitik", BarChart3)}
          {tabBtn("enquiries", "Pertanyaan", Inbox)}
          {tabBtn("content", "Kandungan Laman", FileEdit)}
          {tabBtn("gallery", "Galeri", Images)}
        </div>
        {tab === "analytics" ? <AnalyticsDashboard /> : tab === "enquiries" ? <EnquiriesTable /> : tab === "gallery" ? <GalleryManager /> : <ContentEditor />}
      </main>
    </div>
  );
}
